/**
 * Pure helpers for the Project Hours donut chart.
 * Uses the same displayed project-hours rows as the dashboard card — no new query.
 */

export const PROJECT_HOURS_CHART_COLORS = [
  "#c9a84c",
  "#5b8def",
  "#4aa3a0",
  "#b07d4f",
  "#6b9e78",
  "#8a8f98",
];

/**
 * Verbose hours format used by the KPI dashboard Project Hours card.
 * Example: "109 hrs 25 min"
 */
export function formatProjectHoursDisplay(decimalHours) {
  const totalMinutes = Math.round(Number(decimalHours || 0) * 60);
  const hrs = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;

  if (hrs === 0) return `${mins} min`;
  if (mins === 0) return `${hrs} hr${hrs !== 1 ? "s" : ""}`;

  return `${hrs} hr${hrs !== 1 ? "s" : ""} ${mins} min`;
}

export function formatDisplayedProjectHoursPercent(percent) {
  const value = Number(percent || 0);
  const rounded = Math.round(value * 10) / 10;
  const text = Number.isInteger(rounded)
    ? String(rounded)
    : rounded.toFixed(1);
  return `${text}% of displayed project hours`;
}

/**
 * Build chart slices from the currently displayed Project Hours rows.
 * Percentages are relative to the sum of displayed hours only.
 */
export function buildProjectHoursChartSlices(projects = []) {
  const rows = (projects || [])
    .map((project, index) => ({
      key: project.project_id || project.name || `project-${index}`,
      project_id: project.project_id || null,
      name: project.name || "Unknown Project",
      hrs: Number(project.hrs || 0),
      color: PROJECT_HOURS_CHART_COLORS[index % PROJECT_HOURS_CHART_COLORS.length],
    }))
    .filter((row) => Number.isFinite(row.hrs) && row.hrs > 0);

  const totalHours = rows.reduce((sum, row) => sum + row.hrs, 0);

  if (totalHours <= 0) {
    return { slices: [], totalHours: 0 };
  }

  let cumulative = 0;
  const slices = rows.map((row) => {
    const startRatio = cumulative / totalHours;
    cumulative += row.hrs;
    const endRatio = cumulative / totalHours;
    const percent = (row.hrs / totalHours) * 100;

    return {
      ...row,
      percent,
      percentLabel: formatDisplayedProjectHoursPercent(percent),
      hoursLabel: formatProjectHoursDisplay(row.hrs),
      startRatio,
      endRatio,
    };
  });

  return {
    slices,
    totalHours,
    totalHoursLabel: formatProjectHoursDisplay(totalHours),
  };
}

function polarToCartesian(cx, cy, radius, angleRad) {
  return {
    x: cx + radius * Math.cos(angleRad),
    y: cy + radius * Math.sin(angleRad),
  };
}

/**
 * Build an SVG donut-slice path. Angles are ratios 0..1 from 12 o'clock, clockwise.
 */
export function describeDonutSlicePath({
  cx,
  cy,
  outerRadius,
  innerRadius,
  startRatio,
  endRatio,
}) {
  const fullCircle = Math.abs(endRatio - startRatio) >= 0.999999;
  const startAngle = startRatio * Math.PI * 2 - Math.PI / 2;
  const endAngle = fullCircle
    ? startAngle + Math.PI * 2 - 0.0001
    : endRatio * Math.PI * 2 - Math.PI / 2;

  const outerStart = polarToCartesian(cx, cy, outerRadius, startAngle);
  const outerEnd = polarToCartesian(cx, cy, outerRadius, endAngle);
  const innerStart = polarToCartesian(cx, cy, innerRadius, endAngle);
  const innerEnd = polarToCartesian(cx, cy, innerRadius, startAngle);
  const largeArc = endAngle - startAngle > Math.PI ? 1 : 0;

  return [
    `M ${outerStart.x} ${outerStart.y}`,
    `A ${outerRadius} ${outerRadius} 0 ${largeArc} 1 ${outerEnd.x} ${outerEnd.y}`,
    `L ${innerStart.x} ${innerStart.y}`,
    `A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${innerEnd.x} ${innerEnd.y}`,
    "Z",
  ].join(" ");
}
